import { createAbuseResource } from "./resource-abuse";
import { createAdminResource } from "./resource-admin";
import { createAppConfigResource } from "./resource-app-config";
import { createApplicationsResource } from "./resource-applications";
import { createInvitesResource } from "./resource-invites";
import { createTalentResource } from "./resource-talent";
import { createAuthResource } from "./resource-auth";
import { createBlacklistResource } from "./resource-blacklist";
import { createCatalogsResource } from "./resource-catalogs";
import { createConsentsResource } from "./resource-consents";
import { createDevicesResource } from "./resource-devices";
import { createGeocodeResource } from "./resource-geocode";
import { createIdentityResource } from "./resource-identity";
import { createKycResource } from "./resource-kyc";
import { createMembersResource } from "./resource-members";
import { createNotificationsResource } from "./resource-notifications";
import { createOrgAddressesResource } from "./resource-org-addresses";
import { createOrgsResource } from "./resource-orgs";
import { createPostingTemplatesResource } from "./resource-posting-templates";
import { createPostingsResource } from "./resource-postings";
import { createRatingsResource } from "./resource-ratings";
import { createCreditPurchasesResource } from "./resource-interview-credit-purchases";
import { createSavedResource } from "./resource-saved";
import { createUsersResource } from "./resource-users";
import { createPublicResource } from "./resource-public";
import type {
  RequestContext,
  RequestLogInfo,
  ResponseLogInfo,
  ErrorLogInfo,
} from "./http";

export type ClientConfig = {
  baseUrl: string;
  getAccessToken?: () => Promise<string | null | undefined>;
  onUnauthorized?: () => boolean | void | Promise<boolean | void>;
  extraHeaders?: Record<string, string>;
  getExtraHeaders?: () => Record<string, string> | undefined;
  timeoutMs?: number;
  // Observability — fired around every request. Hook these into your
  // logger to capture method/path/status/duration. Errors thrown from
  // these callbacks are swallowed so logging never breaks the request.
  onRequest?: (info: RequestLogInfo) => void;
  onResponse?: (info: ResponseLogInfo) => void;
  onError?: (info: ErrorLogInfo) => void;
};

export function createClient(config: ClientConfig) {
  if (!config.baseUrl) {
    throw new Error("createClient: baseUrl is required");
  }

  const ctx: RequestContext = {
    baseUrl: config.baseUrl.replace(/\/$/, ""),
    getAccessToken: config.getAccessToken ?? (async () => null),
    onUnauthorized: config.onUnauthorized,
    extraHeaders: config.extraHeaders,
    getExtraHeaders: config.getExtraHeaders,
    timeoutMs: config.timeoutMs ?? 15_000,
    onRequest: config.onRequest,
    onResponse: config.onResponse,
    onError: config.onError,
  };

  return {
    auth: createAuthResource(ctx),
    users: createUsersResource(ctx),
    kyc: createKycResource(ctx),
    identity: createIdentityResource(ctx),
    orgs: createOrgsResource(ctx),
    orgAddresses: createOrgAddressesResource(ctx),
    members: createMembersResource(ctx),
    creditPurchases: createCreditPurchasesResource(ctx),
    postings: createPostingsResource(ctx),
    postingTemplates: createPostingTemplatesResource(ctx),
    applications: createApplicationsResource(ctx),
    invites: createInvitesResource(ctx),
    talent: createTalentResource(ctx),
    ratings: createRatingsResource(ctx),
    abuse: createAbuseResource(ctx),
    blacklist: createBlacklistResource(ctx),
    notifications: createNotificationsResource(ctx),
    saved: createSavedResource(ctx),
    consents: createConsentsResource(ctx),
    catalogs: createCatalogsResource(ctx),
    public: createPublicResource(ctx),
    devices: createDevicesResource(ctx),
    geocode: createGeocodeResource(ctx),
    admin: createAdminResource(ctx),
    appConfig: createAppConfigResource(ctx),
  };
}

export type ChotcaClient = ReturnType<typeof createClient>;
